import type { LandingPageContent } from "@brookbush/types";

import type { GetStaticProps } from "next";
import Script from "next/script";
import { NextSeo } from "next-seo";
import type { PropsWithChildren } from "react";
import type { WebSite, WithContext } from "schema-dts";

import HomeExplorer from "../components/HomeExplorer";
import LandingPageAccreditations from "../info/components/LandingPageAccreditations";
import LandingPageModules from "../info/components/LandingPageModules";
import LandingPageTestimonials from "../info/components/LandingPageTestimonials";
import TeamMembershipFeaturesCard from "./components/TeamMembershipFeaturesCard";
import Faqs from "~/components/Faqs";
import Footer from "~/components/Footer";
import FullWidthCTA from "~/components/FullWidthCTA";
import Hero from "~/components/Hero";
import LandingPagePartners from "~/components/LandingPagePartners";
import Navbar from "~/components/Navbar";
import RecentSignupsToasts from "~/components/RecentSignupsToasts";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

const TeamMembershipPage: NextPageWithProps<{
	landingPageContent: LandingPageContent;
}> = ({ landingPageContent }) => {
	return (
		<>
			<NextSeo
				title={landingPageContent.title}
				description={landingPageContent.description}
				canonical={`${process.env.NEXT_PUBLIC_SITE_URL}`}
				openGraph={{
					title: landingPageContent.title,
					url: `${process.env.NEXT_PUBLIC_SITE_URL}`,
					description: landingPageContent.description,
					images: [
						{
							url: landingPageContent.thumbnail.url,
							width: 800,
							height: 600,
							alt: landingPageContent.title,
						},
					],
				}}
			/>

			<Script
				type="application/ld+json"
				id="json-ld"
				dangerouslySetInnerHTML={{
					__html: JSON.stringify({
						"@context": "https://schema.org",
						"@type": "WebSite",
						url: "https://www.brookbushinstitute.com",
						name: "Brookbush Institute",
						// TODO: Add SearchAction
					} as WithContext<WebSite>),
				}}
			/>

			<main className="-mt-16">
				<Navbar isHomePage />

				<RecentSignupsToasts count={5} />

				<Hero
					isTeamMembership
					title="3 Certifications, 200+ Courses, 500+ Videos and so much more for your team."
					imageUrl={`${process.env.NEXT_PUBLIC_BLOB_STORAGE_URL}/team-membership-landing-page.jpg`}
					link={{
						href: "/become-a-member?type=team",
						label: "Become a team member",
					}}
				/>

				<LandingPagePartners
					partners={landingPageContent.partners}
					customers={landingPageContent.customers}
				/>

				<TeamMembershipFeaturesCard />

				<HomeExplorer />

				<LandingPageAccreditations
					accreditations={landingPageContent.accreditations}
				/>

				<LandingPageModules
					certification={landingPageContent.linkedCertification as any}
				/>

				<LandingPageTestimonials
					testimonials={landingPageContent.testimonials.slice(0, 3)}
				/>

				{landingPageContent.faqs && <Faqs faqs={landingPageContent.faqs} />}

				<FullWidthCTA />

				<Footer />
			</main>
		</>
	);
};

// eslint-disable-next-line react/display-name
TeamMembershipPage.Layout = ({ children }: PropsWithChildren<{}>) => (
	<>{children}</>
);

export const getStaticProps: GetStaticProps = async () => {
	try {
		const landingPageContent = await fetcher(`/landing-pages/homepage`);

		if (!landingPageContent) {
			return {
				notFound: true,
			};
		}

		return {
			props: {
				landingPageContent,
			},
		};
	} catch (_error) {
		return {
			props: {
				error: "Failed to load data",
			},
		};
	}
};

export default TeamMembershipPage;
